export type CsvColumn<T> = {
  header: string;
  value: (row: T) => string | number | null | undefined;
};

/**
 * RFC 4180: a cell is quoted when it holds a comma, a quote or a newline, and
 * quotes inside it are doubled. A leading/trailing space is quoted too, since
 * spreadsheets otherwise trim it away.
 */
const escapeCell = (value: string | number | null | undefined) => {
  const text = value === null || value === undefined ? "" : String(value);

  if (/[",\r\n]/.test(text) || text !== text.trim()) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
};

export const toCsv = <T>(columns: CsvColumn<T>[], rows: T[]) =>
  [
    columns.map((c) => escapeCell(c.header)).join(","),
    ...rows.map((row) =>
      columns.map((c) => escapeCell(c.value(row))).join(","),
    ),
  ].join("\r\n");

/**
 * Hands the browser a CSV file.
 *
 * The BOM is not decoration: without it Excel reads the file as the system
 * codepage and turns every Arabic and Turkish character into mojibake.
 */
export const downloadCsv = (filename: string, content: string) => {
  const blob = new Blob([`﻿${content}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);

  // Revoking immediately can cancel the download in some browsers; the next
  // frame is late enough for it to have started.
  requestAnimationFrame(() => window.URL.revokeObjectURL(url));
};
